package com.tregz.robbins

import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.draw.paint
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInRoot
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalInspectionMode
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import tregz.robbins.generated.resources.Res
import tregz.robbins.generated.resources.banana1
import tregz.robbins.generated.resources.banana2

// ── Design tokens ─────────────────────────────────────────────────────────────
private val Cream  = Color(0xFFF5F2EC)
private val Cream2 = Color(0xFFECE8DF)
private val Cream3 = Color(0xFFE2DDD0)
private val Paper  = Color(0xFFFBF9F4)
private val Ink    = Color(0xFF16140F)
private val Ink2   = Color(0xFF3A362D)
private val Ink3   = Color(0xFF6B6557)
private val Accent = Color(0xFFC9583A)
private val Rule   = Color(0x1F16140F)
private val Rule2  = Color(0x3816140F)

enum class Section {
    Home, About, Portfolio, Contact
}

@Composable
fun Jerome(onBack: () -> Unit) {
    val geist = GeistFamily
    val mono  = GeistMonoFamily
    val serif = InstrumentSerifFamily

    val scrollState = rememberScrollState()
    val scope       = rememberCoroutineScope()
    val density     = LocalDensity.current
    val navPx       = remember(density) { with(density) { 65.dp.toPx().toInt() } }

    // Stable section scroll offsets: positionInRoot().y (layout coords, unaffected by scroll)
    // minus nav bar height gives the exact animateScrollTo target for each section.
    var aboutY  by remember { mutableStateOf(0) }
    var skillsY by remember { mutableStateOf(0) }
    var contactY by remember { mutableStateOf(0) }

    Box(modifier = Modifier.fillMaxSize().background(Cream)) {
        // Max-width centering wrapper — matches HTML max-width container behaviour
        Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.TopCenter) {
            Column(
                modifier = Modifier
                    .widthIn(max = 1100.dp)
                    .fillMaxWidth()
                    .verticalScroll(scrollState)
                    .padding(top = 65.dp)
            ) {
                JeromeHero(geist, mono, serif)
                JeromeMarquee(serif)
                Box(modifier = Modifier.onGloballyPositioned { coords ->
                    aboutY = (coords.positionInRoot().y - navPx).toInt().coerceAtLeast(0)
                }) { JeromeAbout(geist, mono, serif) }


                Box(modifier = Modifier.onGloballyPositioned { coords ->
                    skillsY = (coords.positionInRoot().y - navPx).toInt().coerceAtLeast(0)
                }) { JeromeSkills(geist, mono, serif) }

                JeromeExpertise(geist, mono, serif)

                Box(modifier = Modifier.onGloballyPositioned { coords ->
                    contactY = (coords.positionInRoot().y - navPx).toInt().coerceAtLeast(0)
                }) { JeromeContact(geist, mono, serif) }
                JeromeFooter(mono)
            }
        }
        JeromeNavBar(
            onBack    = onBack,
            geist     = geist, mono = mono, serif = serif,
            onNavigate = { section ->
                scope.launch {
                    when (section) {
                        Section.About  -> scrollState.animateScrollTo(aboutY)
                        Section.Portfolio -> scrollState.animateScrollTo(skillsY)
                        Section.Contact   -> scrollState.animateScrollTo(contactY)
                        else -> scrollState.animateScrollTo(0)
                    }
                }
            }
        )
    }
}

// ── Nav bar ───────────────────────────────────────────────────────────────────

@Composable
private fun JeromeNavBar(
    onBack: () -> Unit,
    geist: androidx.compose.ui.text.font.FontFamily,
    mono: androidx.compose.ui.text.font.FontFamily,
    serif: androidx.compose.ui.text.font.FontFamily,
    onNavigate: (Section) -> Unit = {},
) {
    BoxWithConstraints {
        val isWide = maxWidth > 600.dp
        Column(modifier = Modifier.fillMaxWidth()) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(65.dp)
                    .background(Cream.copy(alpha = 0.90f))
                    .padding(horizontal = 20.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                // Left: back button + divider + brand
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                    // Back button — always visible
                    Row(
                        modifier = Modifier
                            .clip(RoundedCornerShape(999.dp))
                            .border(0.75.dp, Rule2, RoundedCornerShape(999.dp))
                            .clickable(onClick = onBack)
                            .padding(horizontal = 14.dp, vertical = 8.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(6.dp)
                    ) {
                        Text("←", fontFamily = geist, color = Ink2, fontSize = 14.sp, fontWeight = FontWeight.Medium)
                        Text("Retour", fontFamily = geist, color = Ink2, fontSize = 13.sp, fontWeight = FontWeight.Medium, letterSpacing = (-0.1).sp)
                    }
                    Box(modifier = Modifier.width(0.5.dp).height(28.dp).background(Rule2))
                    // Brand mark
                    Box(modifier = Modifier.size(36.dp).clip(RoundedCornerShape(10.dp)).background(Ink)) {
                        Box(modifier = Modifier.align(Alignment.BottomCenter).fillMaxWidth().height(5.dp).background(Accent))
                        Text(
                            "J",
                            fontFamily = serif, color = Cream, fontSize = 22.sp,
                            fontStyle = FontStyle.Italic,
                            modifier = Modifier.align(Alignment.Center).padding(bottom = 3.dp)
                        )
                    }
                    if (isWide) {
                        Column {
                            Text("Jérôme Robbins", fontFamily = geist, color = Ink, fontSize = 14.sp, fontWeight = FontWeight.SemiBold, letterSpacing = (-0.2).sp, lineHeight = 17.sp)
                            Text("DÉVELOPPEUR MOBILE", fontFamily = mono, color = Ink3, fontSize = 9.sp, letterSpacing = 1.2.sp, lineHeight = 13.sp)
                        }
                    }
                }

                // Nav links + CTA — visible on wide screens only
                if (isWide) {
                    Row(horizontalArrangement = Arrangement.spacedBy(4.dp), verticalAlignment = Alignment.CenterVertically) {
                        val links = listOf(
                            "Accueil" to Section.Home,
                            "À propos" to Section.About,
                            "Portfolio" to Section.Portfolio
                        )
                        links.forEach { (label, section) ->
                            Text(
                                label,
                                fontFamily = geist, color = Ink2, fontSize = 14.sp,
                                letterSpacing = (-0.1).sp,
                                modifier = Modifier
                                    .clip(RoundedCornerShape(999.dp))
                                    .border(0.5.dp, Rule2, RoundedCornerShape(999.dp))
                                    .clickable { onNavigate(section) }
                                    .padding(horizontal = 14.dp, vertical = 8.dp)
                            )
                        }
                        Spacer(Modifier.width(6.dp))
                        // "Working together" CTA — navigates back
                        Button(
                            onClick = { onNavigate(Section.Contact) },
                            shape = CircleShape,
                            colors = ButtonDefaults.buttonColors(containerColor = Ink, contentColor = Cream),
                            contentPadding = PaddingValues(horizontal = 18.dp, vertical = 9.dp)
                        ) {
                            Text("Travaillons ensemble →", fontFamily = geist, fontSize = 13.sp, fontWeight = FontWeight.Medium)
                        }
                    }
                }
            }
            Box(modifier = Modifier.fillMaxWidth().height(0.5.dp).background(Rule))
        }
    }
}

// ── Hero ─────────────────────────────────────────────────────────────────────

@Composable
private fun JeromeHero(
    geist: androidx.compose.ui.text.font.FontFamily,
    mono: androidx.compose.ui.text.font.FontFamily,
    serif: androidx.compose.ui.text.font.FontFamily,
) {
    val infiniteTransition = rememberInfiniteTransition(label = "pulse")
    val pulse by infiniteTransition.animateFloat(
        initialValue = 0f, targetValue = 1f,
        animationSpec = infiniteRepeatable(tween(2200, easing = LinearEasing), RepeatMode.Restart),
        label = "pulse"
    )

    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        val isWide = maxWidth > 760.dp
        if (isWide) {
            Column(modifier = Modifier.fillMaxWidth().padding(horizontal = 24.dp)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(64.dp),
                    verticalAlignment = Alignment.Bottom
                ) {
                    Column(modifier = Modifier.weight(1.15f).padding(top = 40.dp)) {
                        HeroEyebrow(pulse, mono)
                        HeroHeadline(geist, serif)
                        HeroLede(geist)
                        HeroActions(geist)
                        Spacer(Modifier.height(40.dp))
                    }
                    Box(modifier = Modifier.weight(0.85f), contentAlignment = Alignment.BottomEnd) {
                        DeviceSection(geist, mono, serif)
                    }
                }
                HeroStats(geist, mono, serif)
                Spacer(Modifier.height(40.dp))
            }
        } else {
            Column(modifier = Modifier.fillMaxWidth()) {
                Column(modifier = Modifier.padding(horizontal = 24.dp)) {
                    Spacer(Modifier.height(40.dp))
                    HeroEyebrow(pulse, mono)
                    HeroHeadline(geist, serif)
                    HeroLede(geist)
                    HeroActions(geist)
                    Spacer(Modifier.height(32.dp))
                }
                DeviceSection(geist, mono, serif)
                Column(modifier = Modifier.padding(horizontal = 24.dp)) {
                    HeroStats(geist, mono, serif)
                    Spacer(Modifier.height(40.dp))
                }
            }
        }
    }
}

@Composable
private fun HeroEyebrow(pulse: Float, mono: androidx.compose.ui.text.font.FontFamily) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
        modifier = Modifier.padding(bottom = 28.dp)
    ) {
        Box(contentAlignment = Alignment.Center, modifier = Modifier.size(20.dp)) {
            Box(modifier = Modifier.size((7f + pulse * 14f).dp).clip(CircleShape).background(Accent.copy(alpha = (1f - pulse) * 0.5f)))
            Box(modifier = Modifier.size(7.dp).clip(CircleShape).background(Accent))
        }
        Text("Indisponible pour projets", fontFamily = mono, color = Ink3, fontSize = 11.sp, letterSpacing = 0.5.sp)
        Box(modifier = Modifier.width(32.dp).height(1.dp).background(Rule2))
    }
}

@Composable
private fun HeroHeadline(
    geist: androidx.compose.ui.text.font.FontFamily,
    serif: androidx.compose.ui.text.font.FontFamily,
) {
    Text(
        buildAnnotatedString {
            withStyle(SpanStyle(fontFamily = geist, fontWeight = FontWeight.Medium, color = Ink)) { append("Développeur mobile, ") }
            withStyle(SpanStyle(fontFamily = serif, color = Accent, fontStyle = FontStyle.Italic, fontWeight = FontWeight.Normal)) { append("spécialisé") }
            withStyle(SpanStyle(fontFamily = geist, fontWeight = FontWeight.Medium, color = Ink)) { append(" en applications natives ") }
            withStyle(SpanStyle(fontFamily = geist, fontWeight = FontWeight.Medium, color = Ink, textDecoration = TextDecoration.Underline)) { append("iOS") }
            withStyle(SpanStyle(fontFamily = geist, fontWeight = FontWeight.Medium, color = Ink)) { append(" & ") }
            withStyle(SpanStyle(fontFamily = geist, fontWeight = FontWeight.Medium, color = Ink, textDecoration = TextDecoration.Underline)) { append("Android") }
            withStyle(SpanStyle(fontFamily = geist, fontWeight = FontWeight.Medium, color = Ink)) { append(".") }
        },
        fontSize = 36.sp, lineHeight = 38.sp, letterSpacing = (-1.0).sp,
        modifier = Modifier.padding(bottom = 20.dp)
    )
}

@Composable
private fun HeroLede(geist: androidx.compose.ui.text.font.FontFamily) {
    Text(
        "Très actif, je cumule des réalisations qui m'ont permis d'acquérir des compétences variées en conception d'interfaces et en programmation.",
        fontFamily = geist, color = Ink2, fontSize = 17.sp, lineHeight = 28.sp,
        modifier = Modifier.padding(bottom = 28.dp)
    )
}

@Composable
private fun HeroActions(geist: androidx.compose.ui.text.font.FontFamily) {
    Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
        CtaButton("En savoir plus →", geist)
        GhostButton("Voir le portfolio", geist)
    }
}

@Composable
private fun HeroStats(
    geist: androidx.compose.ui.text.font.FontFamily,
    mono: androidx.compose.ui.text.font.FontFamily,
    serif: androidx.compose.ui.text.font.FontFamily,
) {
    Box(modifier = Modifier.fillMaxWidth().height(0.5.dp).background(Rule))
    Row(modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min)) {
        StatCell("ANNÉES EN MOBILE", buildAnnotatedString {
            withStyle(SpanStyle(fontFamily = geist, fontWeight = FontWeight.Medium, color = Ink, fontSize = 30.sp)) { append("10") }
            withStyle(SpanStyle(fontFamily = serif, color = Accent, fontStyle = FontStyle.Italic, fontWeight = FontWeight.Normal, fontSize = 30.sp)) { append("+") }
        }, "Android & iOS natif", mono, Modifier.weight(1f))
        Box(modifier = Modifier.width(0.5.dp).fillMaxHeight().background(Rule))
        StatCell("LANGAGES", buildAnnotatedString {
            withStyle(SpanStyle(fontFamily = geist, fontWeight = FontWeight.Medium, color = Ink, fontSize = 30.sp)) { append("10") }
            withStyle(SpanStyle(fontFamily = serif, color = Accent, fontStyle = FontStyle.Italic, fontWeight = FontWeight.Normal, fontSize = 30.sp)) { append("+") }
        }, "Kotlin, Swift…", mono, Modifier.weight(1f).padding(start = 16.dp))
    }
    Box(modifier = Modifier.fillMaxWidth().height(0.5.dp).background(Rule))
    Row(modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min)) {
        StatCell("PLATEFORMES", buildAnnotatedString {
            withStyle(SpanStyle(fontFamily = geist, fontWeight = FontWeight.Medium, color = Ink, fontSize = 22.sp)) { append("iOS · Android") }
        }, "+ Web", mono, Modifier.weight(1f))
        Box(modifier = Modifier.width(0.5.dp).fillMaxHeight().background(Rule))
        StatCell("BASÉ À", buildAnnotatedString {
            withStyle(SpanStyle(fontFamily = geist, fontWeight = FontWeight.Medium, color = Ink, fontSize = 26.sp)) { append("Québec, ") }
            withStyle(SpanStyle(fontFamily = serif, color = Accent, fontStyle = FontStyle.Italic, fontWeight = FontWeight.Normal, fontSize = 26.sp)) { append("CA") }
        }, "Disponible à distance", mono, Modifier.weight(1f).padding(start = 16.dp))
    }
    Box(modifier = Modifier.fillMaxWidth().height(0.5.dp).background(Rule))
}

@Composable
private fun StatCell(
    label: String, value: AnnotatedString, sub: String,
    mono: androidx.compose.ui.text.font.FontFamily,
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier.padding(end = 12.dp, top = 20.dp, bottom = 20.dp)) {
        Text(label, fontFamily = mono, color = Ink3, fontSize = 10.5.sp, letterSpacing = 1.2.sp, lineHeight = 14.sp)
        Spacer(Modifier.height(14.dp))
        Text(value, lineHeight = 30.sp, letterSpacing = (-0.5).sp)
        Spacer(Modifier.height(8.dp))
        Text(sub, color = Ink3, fontSize = 12.5.sp)
    }
}

// ── Device section — phone mockup + floating chips ────────────────────────────

@Composable
private fun DeviceSection(
    geist: androidx.compose.ui.text.font.FontFamily,
    mono: androidx.compose.ui.text.font.FontFamily,
    serif: androidx.compose.ui.text.font.FontFamily,
) {
    Box(
        modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 24.dp),
        contentAlignment = Alignment.TopEnd
    ) {
        // Device + chips container — right-aligned
        Box(modifier = Modifier.align(Alignment.TopEnd)) {
            // Phone
            PhoneMockup(
                geist = geist, mono = mono, serif = serif,
                modifier = Modifier.padding(top = 36.dp, end = 12.dp).rotate(-3f)
            )
            // c1 chip: top right — same tilt as phone
            TechChip("Kotlin · Android", Color(0xFF7F52FF), geist,
                modifier = Modifier.align(Alignment.TopEnd).offset(x = (-12).dp, y = 4.dp).rotate(-3f))
            // c2 chip: right side — slight counter-tilt
            TechChip("Swift · iOS", Color(0xFFF05138), geist,
                modifier = Modifier.align(Alignment.CenterEnd).offset(y = 30.dp).rotate(3f))
            // c3 chip: lower left — same tilt as phone
            TechChip("Compose · Multiplatform", Accent, geist,
                modifier = Modifier.align(Alignment.BottomStart).offset(x = 4.dp, y = (-72).dp).rotate(-3f))
        }
    }
}

@Composable
private fun TechChip(
    text: String,
    swatchColor: Color,
    geist: androidx.compose.ui.text.font.FontFamily,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier
            .clip(CircleShape)
            .background(Paper)
            .border(0.5.dp, Rule, CircleShape)
            .padding(horizontal = 14.dp, vertical = 9.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(9.dp)
    ) {
        Box(modifier = Modifier.size(9.dp).clip(CircleShape).background(swatchColor))
        Text(text, fontFamily = geist, color = Ink, fontSize = 13.sp, fontWeight = FontWeight.Medium)
    }
}

@Composable
private fun PhoneMockup(
    geist: androidx.compose.ui.text.font.FontFamily,
    mono: androidx.compose.ui.text.font.FontFamily,
    serif: androidx.compose.ui.text.font.FontFamily,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier
            .width(220.dp)
            .height(448.dp)
            .clip(RoundedCornerShape(37.dp))
            .background(brush = Brush.verticalGradient(colors = listOf(Color(0xFF1C1A16), Color(0xFF0B0A08))))
            .padding(8.dp)
    ) {
        // Screen
        Box(
            modifier = Modifier
                .fillMaxSize()
                .clip(RoundedCornerShape(30.dp))
                .background(brush = Brush.linearGradient(
                    colors = listOf(Color(0xFFFAF6EE), Color(0xFFF0EAD9)),
                    start = Offset(0f, 0f), end = Offset(300f, 700f)
                ))
        ) {
            Column {
                // Status bar
                Row(
                    modifier = Modifier.fillMaxWidth().height(47.dp).padding(top = 16.dp, start = 22.dp, end = 22.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text("9:41", fontFamily = mono, color = Ink, fontSize = 10.sp)
                    Row(verticalAlignment = Alignment.Bottom, horizontalArrangement = Arrangement.spacedBy(2.dp)) {
                        listOf(4, 6, 8, 10).forEach { h ->
                            Box(modifier = Modifier.width(3.dp).height(h.dp).background(Ink, RoundedCornerShape(1.dp)))
                        }
                    }
                }

                // App content
                Column(modifier = Modifier.padding(horizontal = 16.dp, vertical = 10.dp)) {
                    Text("PROFILE · V2.4", fontFamily = mono, color = Ink3, fontSize = 9.sp, letterSpacing = 1.sp)

                    // App headline with serif italic accent
                    Text(
                        buildAnnotatedString {
                            append("Salut, ")
                            append(" ici ")
                            withStyle(SpanStyle(fontFamily = serif, color = Accent, fontStyle = FontStyle.Italic)) { append("Jerome") }

                            append(".")
                        },
                        fontFamily = serif, fontSize = 22.sp, lineHeight = 24.sp, letterSpacing = (-0.5).sp,
                        modifier = Modifier.padding(top = 6.dp, bottom = 12.dp)
                    )

                    // Skill bars card
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(14.dp))
                            .background(Color.White.copy(alpha = 0.65f))
                            .border(0.5.dp, Color(0xFF16140F).copy(alpha = 0.08f), RoundedCornerShape(14.dp))
                            .padding(11.dp)
                    ) {
                        AppSkillRow("K", Color(0xFF7F52FF), "Kotlin", 0.92f, geist, mono)
                        Box(modifier = Modifier.fillMaxWidth().height(0.5.dp).background(Color(0xFF16140F).copy(alpha = 0.06f)))
                        AppSkillRow("S", Color(0xFFF05138), "Swift", 0.88f, geist, mono)
                        Box(modifier = Modifier.fillMaxWidth().height(0.5.dp).background(Color(0xFF16140F).copy(alpha = 0.06f)))
                        AppSkillRow("J", Color(0xFF007396), "Firebase", 0.80f, geist, mono)
                        Box(modifier = Modifier.fillMaxWidth().height(0.5.dp).background(Color(0xFF16140F).copy(alpha = 0.06f)))
                        AppSkillRow("P", Color(0xFF3776AB), "Compose", 0.70f, geist, mono)
                    }

                    // CTA button
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(top = 11.dp)
                            .clip(CircleShape)
                            .background(Ink)
                            .padding(vertical = 10.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Text("Écrivez-moi →", fontFamily = geist, color = Cream, fontSize = 11.sp, fontWeight = FontWeight.Medium)
                    }
                }
            }
        }

        // Dynamic Island — rendered last to appear on top
        Box(
            modifier = Modifier
                .align(Alignment.TopCenter)
                .padding(top = 15.dp)
                .width(75.dp)
                .height(21.dp)
                .clip(RoundedCornerShape(10.dp))
                .background(Color.Black)
        )
    }
}

@Composable
private fun AppSkillRow(
    ico: String,
    color: Color,
    name: String,
    progress: Float,
    geist: androidx.compose.ui.text.font.FontFamily,
    mono: androidx.compose.ui.text.font.FontFamily,
) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = 9.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            Box(modifier = Modifier.size(24.dp).clip(RoundedCornerShape(7.dp)).background(color), contentAlignment = Alignment.Center) {
                Text(ico, fontFamily = mono, color = Color.White, fontSize = 9.sp, fontWeight = FontWeight.Bold)
            }
            Text(name, fontFamily = geist, color = Ink, fontSize = 11.sp, fontWeight = FontWeight.Medium)
        }
        Box(modifier = Modifier.width(48.dp).height(6.dp).clip(CircleShape).background(Color(0xFF16140F).copy(alpha = 0.06f))) {
            Box(modifier = Modifier.fillMaxHeight().fillMaxWidth(progress).clip(CircleShape).background(Accent))
        }
    }
}

// ── Button helpers ────────────────────────────────────────────────────────────

@Composable
private fun CtaButton(text: String, geist: androidx.compose.ui.text.font.FontFamily, onClick: () -> Unit = {}) {
    Button(
        onClick = onClick, shape = CircleShape,
        colors = ButtonDefaults.buttonColors(containerColor = Ink, contentColor = Cream),
        contentPadding = PaddingValues(horizontal = 20.dp, vertical = 10.dp)
    ) {
        Text(text, fontFamily = geist, fontSize = 13.5.sp, fontWeight = FontWeight.Medium, letterSpacing = (-0.2).sp)
    }
}

@Composable
private fun GhostButton(text: String, geist: androidx.compose.ui.text.font.FontFamily, darkBg: Boolean = false) {
    val borderColor = if (darkBg) Cream.copy(alpha = 0.25f) else Rule2
    val textColor   = if (darkBg) Cream else Ink
    Row(
        modifier = Modifier
            .clip(CircleShape)
            .border(0.75.dp, borderColor, CircleShape)
            .padding(horizontal = 18.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Box(modifier = Modifier.size(7.dp).clip(CircleShape).background(Accent))
        Text(text, fontFamily = geist, color = textColor, fontSize = 13.5.sp, fontWeight = FontWeight.Medium)
    }
}

// ── Marquee ───────────────────────────────────────────────────────────────────

@Composable
private fun JeromeMarquee(serif: androidx.compose.ui.text.font.FontFamily) {
    val items = listOf(
        "Kotlin" to false, "Swift" to true, "Compose" to false, "Firebase" to true, "Claude" to false, "Gemini" to true
    )
    val seq = items + items
    val scrollState = rememberScrollState()

    LaunchedEffect(Unit) {
        while (true) {
            while (scrollState.maxValue == 0) delay(50)
            val half = scrollState.maxValue / 2
            if (half > 0) scrollState.animateScrollTo(half, animationSpec = tween(38000, easing = LinearEasing))
            scrollState.scrollTo(0)
        }
    }

    Box(
        modifier = Modifier.fillMaxWidth()
            .drawBehind {
                drawLine(Rule, Offset(0f, 0f), Offset(size.width, 0f), 0.5f)
                drawLine(Rule, Offset(0f, size.height), Offset(size.width, size.height), 0.5f)
            }
            .background(Cream2)
            .padding(vertical = 20.dp)
    ) {
        Row(modifier = Modifier.horizontalScroll(scrollState, enabled = false), verticalAlignment = Alignment.CenterVertically) {
            Spacer(Modifier.width(24.dp))
            seq.forEach { (name, isEm) ->
                Text(
                    name,
                    fontFamily = serif,
                    color = if (isEm) Accent else Ink,
                    fontSize = 24.sp,
                    fontStyle = if (isEm) FontStyle.Italic else FontStyle.Normal
                )
                Spacer(Modifier.width(48.dp))
                Box(modifier = Modifier.size(6.dp).clip(CircleShape).background(Ink3))
                Spacer(Modifier.width(48.dp))
            }
        }
    }
}

// ── Section helpers ───────────────────────────────────────────────────────────

@Composable
private fun SectionTag(n: String, label: String, mono: androidx.compose.ui.text.font.FontFamily) {
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
        Box(modifier = Modifier.size(24.dp).clip(CircleShape).border(0.5.dp, Rule2, CircleShape), contentAlignment = Alignment.Center) {
            Text(n, fontFamily = mono, color = Ink2, fontSize = 10.sp)
        }
        Text(label.uppercase(), fontFamily = mono, color = Ink3, fontSize = 11.sp, letterSpacing = 1.4.sp)
    }
}

@Composable
private fun SectionTitle(
    text: AnnotatedString,
    geist: androidx.compose.ui.text.font.FontFamily,
) {
    Text(
        text,
        fontFamily = geist, color = Ink, fontSize = 36.sp, fontWeight = FontWeight.Medium,
        lineHeight = 36.sp, letterSpacing = (-1.0).sp,
        modifier = Modifier.padding(top = 14.dp, bottom = 40.dp)
    )
}

@Composable
private fun PhotoPlaceholder(
    drawableResource: DrawableResource,
    cornerLabel: String,
    bottomLabel: String,
    aspectRatio: Float,
    mono: androidx.compose.ui.text.font.FontFamily,
    modifier: Modifier = Modifier
) {
    val isPreview = LocalInspectionMode.current
    Box(
        modifier = modifier.fillMaxWidth().aspectRatio(aspectRatio)
            .clip(RoundedCornerShape(24.dp))
            .background(Cream3)
            .drawBehind {
                var x = -size.height
                while (x < size.width + size.height) {
                    drawLine(Color(0xFF16140F).copy(alpha = 0.08f), Offset(x, 0f), Offset(x + size.height, size.height), strokeWidth = 1.5f)
                    x += 15f
                }
            }.then(
                if (isPreview) Modifier
                else Modifier.paint(painterResource(drawableResource), contentScale = ContentScale.FillWidth)
            )
    ) {
        Text(cornerLabel, fontFamily = mono, color = Ink3, fontSize = 10.5.sp, letterSpacing = 0.6.sp, modifier = Modifier.align(Alignment.TopEnd).padding(18.dp))
        Text(bottomLabel.uppercase(), fontFamily = mono, color = Ink2, fontSize = 11.sp, letterSpacing = 0.8.sp,
            modifier = Modifier.align(Alignment.BottomStart).padding(18.dp).background(Color(0xD9FBF9F4), RoundedCornerShape(8.dp)).padding(horizontal = 10.dp, vertical = 7.dp))
    }
}

// ── About ─────────────────────────────────────────────────────────────────────

@Composable
private fun JeromeAbout(
    geist: androidx.compose.ui.text.font.FontFamily,
    mono: androidx.compose.ui.text.font.FontFamily,
    serif: androidx.compose.ui.text.font.FontFamily,
) {
    Column(modifier = Modifier.fillMaxWidth().padding(horizontal = 24.dp, vertical = 56.dp)) {
        SectionTag("01", "Profil", mono)
        SectionTitle(buildAnnotatedString {
            withStyle(SpanStyle(fontFamily = geist, color = Ink)) { append("À ") }
            withStyle(SpanStyle(fontFamily = serif, color = Accent, fontStyle = FontStyle.Italic, fontWeight = FontWeight.Normal)) { append("propos") }
            withStyle(SpanStyle(fontFamily = geist, color = Ink)) { append(" de moi —\n10 ans à façonner du mobile.") }
        }, geist)

        listOf(
            "Depuis 2021, je dirige le dévelopment des applications mobiles de Simaudio. Ces applications communiquent avec les produits Simaudio, pour les configurer, synchroniser leur états et leurs envoyer des commandes.",
            "Auparavant, j'ai été travailleur autonome, chargé de cours au collégial en développement d'applications mobiles et chargé du développement d'une application mobile pour une entreprise en démarrage",
            "Je suis également actif dans la communauté. Localement, je participe aux rencontres de programmeurs en informatique."
        ).forEach { para ->
            Text(para, fontFamily = geist, color = Ink2, fontSize = 17.sp, lineHeight = 28.sp, modifier = Modifier.padding(bottom = 18.dp))
        }

        MetaRow("Rôle",       "Développeur d'applications mobiles",  "01", geist, mono)
        MetaRow("Formation",  "Informatique, en applications mobiles", "02", geist, mono)
        MetaRow("Langues",    "Français · Anglais",           "03", geist, mono)
        MetaRow("Communauté", "Meetups",      "04", geist, mono)

        Spacer(Modifier.height(40.dp))
        PhotoPlaceholder(Res.drawable.banana2, "img · 4:5", "Portrait — bureau / home office", 4f / 5f, mono)
    }
}

@Composable
private fun MetaRow(key: String, value: String, n: String, geist: androidx.compose.ui.text.font.FontFamily, mono: androidx.compose.ui.text.font.FontFamily) {
    Column {
        Row(modifier = Modifier.fillMaxWidth().padding(vertical = 14.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(24.dp)) {
            Text(key.uppercase(), fontFamily = mono, color = Ink3, fontSize = 11.sp, letterSpacing = 0.8.sp, modifier = Modifier.width(100.dp))
            Text(value, fontFamily = geist, color = Ink, fontSize = 14.sp, fontWeight = FontWeight.Medium, modifier = Modifier.weight(1f))
            Text(n, fontFamily = mono, color = Ink3, fontSize = 11.5.sp)
        }
        Box(modifier = Modifier.fillMaxWidth().height(0.5.dp).background(Rule))
    }
}

// ── Skills ────────────────────────────────────────────────────────────────────

@Composable
private fun JeromeSkills(
    geist: androidx.compose.ui.text.font.FontFamily,
    mono: androidx.compose.ui.text.font.FontFamily,
    serif: androidx.compose.ui.text.font.FontFamily,
) {
    Box(modifier = Modifier.fillMaxWidth().height(0.5.dp).background(Rule))
    Column(modifier = Modifier.fillMaxWidth().padding(horizontal = 24.dp, vertical = 56.dp)) {
        SectionTag("02", "Compétences", mono)
        SectionTitle(buildAnnotatedString {
            withStyle(SpanStyle(fontFamily = geist, color = Ink)) { append("Trois pratiques, ") }
            withStyle(SpanStyle(fontFamily = serif, color = Accent, fontStyle = FontStyle.Italic, fontWeight = FontWeight.Normal)) { append("un seul") }
            withStyle(SpanStyle(fontFamily = geist, color = Ink)) { append(" artisan.") }
        }, geist)

        SkillCard("SK · 01", "Développement web",
            "Sites Internet adaptatifs, propres et performants, pensés pour le mobile.",
            listOf("HTML", "CSS", "JavaScript", "Python", "Firebase"), "w", geist, mono, serif)
        Spacer(Modifier.height(16.dp))
        SkillCard("SK · 02", "Développement natif",
            "Applications mobiles en langage natif, pour la performance et perceptibilité.",
            listOf("Kotlin", "Swift"), "n", geist, mono, serif)
        Spacer(Modifier.height(16.dp))
        SkillCard("SK · 03", "Révision de code",
            "Mise à jour, optimisation et débogage, pour des bases de code qui vieillissent bien.",
            listOf("Code review", "Refactor", "Tests"), "r", geist, mono, serif)
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun SkillCard(
    number: String, title: String, desc: String, tags: List<String>, bg: String,
    geist: androidx.compose.ui.text.font.FontFamily,
    mono: androidx.compose.ui.text.font.FontFamily,
    serif: androidx.compose.ui.text.font.FontFamily,
) {
    Box(modifier = Modifier.fillMaxWidth().clip(RoundedCornerShape(22.dp)).border(0.5.dp, Rule, RoundedCornerShape(22.dp)).background(Paper)) {
        Text(
            bg,
            fontFamily = serif, color = Ink.copy(alpha = 0.04f), fontSize = 200.sp,
            fontStyle = FontStyle.Italic, lineHeight = 180.sp,
            modifier = Modifier.align(Alignment.BottomEnd).offset(x = 12.dp, y = 28.dp)
        )
        Column(modifier = Modifier.padding(28.dp)) {
            Row(modifier = Modifier.fillMaxWidth().padding(bottom = 48.dp), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Text(number, fontFamily = mono, color = Ink3, fontSize = 11.sp, letterSpacing = 1.2.sp)
                Box(modifier = Modifier.size(8.dp).clip(CircleShape).background(Accent.copy(alpha = 0.85f)))
            }
            Text(title, fontFamily = geist, color = Ink, fontSize = 20.sp, fontWeight = FontWeight.Medium, letterSpacing = (-0.3).sp, lineHeight = 24.sp, modifier = Modifier.padding(bottom = 12.dp))
            Text(desc, fontFamily = geist, color = Ink3, fontSize = 14.5.sp, lineHeight = 23.sp, modifier = Modifier.padding(bottom = 24.dp))
            FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                tags.forEach { tag ->
                    Text(tag, fontFamily = mono, color = Ink2, fontSize = 12.sp,
                        modifier = Modifier.clip(RoundedCornerShape(6.dp)).background(Rule).padding(horizontal = 10.dp, vertical = 5.dp))
                }
            }
        }
    }
}

// ── Expertise ─────────────────────────────────────────────────────────────────

private data class Tech(val ico: String, val name: String, val cat: String, val color: Color, val years: String)

private val TechStack = listOf(
    Tech("Kt", "Kotlin",          "Android natif",      Color(0xFF7F52FF), "2018 →"),
    Tech("Sw", "Swift",           "iOS natif",          Color(0xFFF05138), "2019 →"),
    Tech("Jv", "Java",            "Android natif",      Color(0xFF007396), "2015 →"),
    Tech("Py", "Python",          "Nuage",              Color(0xFF3776AB), "2017 →"),
    Tech("Js", "JavaScript",      "Web",                Color(0xFFF7DF1E), "2014 →"),
    Tech("Xa", "Xamarin",         "Multi-platforme",    Color(0xFF61DAFB), "2021 →"),
    Tech("Cm", "Compose",         "Multi-platforme",    Color(0xFF02569B), "2026 →"),
    Tech("Fb", "Firebase",        "Nuage",              Color(0xFFFFCA28), "2018 →"),
)

@Composable
private fun JeromeExpertise(
    geist: androidx.compose.ui.text.font.FontFamily,
    mono: androidx.compose.ui.text.font.FontFamily,
    serif: androidx.compose.ui.text.font.FontFamily,
) {
    Box(modifier = Modifier.fillMaxWidth().height(0.5.dp).background(Rule))
    Column(modifier = Modifier.fillMaxWidth().padding(horizontal = 24.dp, vertical = 56.dp)) {
        SectionTag("03", "Expérience & expertise", mono)
        SectionTitle(buildAnnotatedString {
            withStyle(SpanStyle(fontFamily = geist, color = Ink)) { append("Une ") }
            withStyle(SpanStyle(fontFamily = serif, color = Accent, fontStyle = FontStyle.Italic, fontWeight = FontWeight.Normal)) { append("pile") }
            withStyle(SpanStyle(fontFamily = geist, color = Ink)) { append(" taillée pour le mobile.") }
        }, geist)

        PhotoPlaceholder(Res.drawable.banana1, "img · 5:4", "Appareils — banc d'essai mobile", 5f / 4f, mono, modifier = Modifier.padding(bottom = 24.dp))

        Text("Pour les téléphones et tablettes, je code des applications en langage natif : Kotlin pour Android et Swift pour iOS.",
            fontFamily = geist, color = Ink2, fontSize = 17.sp, lineHeight = 28.sp, modifier = Modifier.padding(bottom = 14.dp))
        Text("Je suis également spécialisé en dévelopment multiplateforme, en Compose.",
            fontFamily = geist, color = Ink2, fontSize = 17.sp, lineHeight = 28.sp, modifier = Modifier.padding(bottom = 32.dp))

        Box(modifier = Modifier.fillMaxWidth().height(0.5.dp).background(Rule))
        TechStack.forEach { tech ->
            TechRow(tech, geist, mono)
            Box(modifier = Modifier.fillMaxWidth().height(0.5.dp).background(Rule))
        }
    }
}

@Composable
private fun TechRow(tech: Tech, geist: androidx.compose.ui.text.font.FontFamily, mono: androidx.compose.ui.text.font.FontFamily) {
    Row(modifier = Modifier.fillMaxWidth().padding(vertical = 18.dp).padding(start = 4.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(16.dp)) {
        Box(modifier = Modifier.size(36.dp).clip(RoundedCornerShape(10.dp)).background(tech.color), contentAlignment = Alignment.Center) {
            Text(tech.ico, fontFamily = mono, color = Color.White, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
        }
        Text(tech.name, fontFamily = geist, color = Ink, fontSize = 16.sp, fontWeight = FontWeight.Medium, letterSpacing = (-0.1).sp, modifier = Modifier.weight(1f))
        Text(tech.cat.uppercase(), fontFamily = mono, color = Ink3, fontSize = 10.sp, letterSpacing = 0.6.sp)
        Text(tech.years, fontFamily = mono, color = Ink2, fontSize = 12.sp)
    }
}

// ── Contact ───────────────────────────────────────────────────────────────────

@Composable
private fun JeromeContact(
    geist: androidx.compose.ui.text.font.FontFamily,
    mono: androidx.compose.ui.text.font.FontFamily,
    serif: androidx.compose.ui.text.font.FontFamily,
) {
    Column(modifier = Modifier.fillMaxWidth().background(Ink).padding(horizontal = 24.dp).padding(top = 56.dp, bottom = 40.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.padding(bottom = 32.dp)) {
            Box(modifier = Modifier.size(7.dp).clip(CircleShape).background(Accent))
            Text("BESOIN D'AIDE AVEC UN PROJET ?", fontFamily = mono, color = Cream.copy(alpha = 0.5f), fontSize = 11.sp, letterSpacing = 1.4.sp)
        }

//        Text(
//            buildAnnotatedString {
//                withStyle(SpanStyle(fontFamily = geist, color = Cream)) { append("Travaillons ") }
//                withStyle(SpanStyle(fontFamily = serif, color = Accent, fontStyle = FontStyle.Italic, fontWeight = FontWeight.Normal)) { append("ensemble") }
//                withStyle(SpanStyle(fontFamily = geist, color = Cream)) { append(".") }
//            },
//            fontSize = 48.sp, fontWeight = FontWeight.Normal, lineHeight = 48.sp, letterSpacing = (-1.5).sp,
//            modifier = Modifier.padding(bottom = 40.dp)
//        )

//        Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.padding(bottom = 56.dp)) {
//            Button(onClick = {}, shape = CircleShape,
//                colors = ButtonDefaults.buttonColors(containerColor = Cream, contentColor = Ink),
//                contentPadding = PaddingValues(horizontal = 22.dp, vertical = 14.dp)) {
//                Text("Écrivez-moi →", fontFamily = geist, fontSize = 15.sp, fontWeight = FontWeight.Medium, letterSpacing = (-0.2).sp)
//            }
//            GhostButton("Voir mon GitHub", geist, darkBg = true)
//        }

        Box(modifier = Modifier.fillMaxWidth().height(0.5.dp).background(Cream.copy(alpha = 0.15f)))
        Spacer(Modifier.height(32.dp))

        ContactBlock("Courriel",       "jerome@tregz.com", "Réponse sous 48 h",          geist, mono)
        Spacer(Modifier.height(28.dp))
        ContactBlock("Basé à",         "Québec, Canada",     "UTC −05:00 · à distance",    geist, mono)
    }
}

@Composable
private fun ContactBlock(label: String, value: String, sub: String, geist: androidx.compose.ui.text.font.FontFamily, mono: androidx.compose.ui.text.font.FontFamily) {
    Column {
        Text(label.uppercase(), fontFamily = mono, color = Cream.copy(alpha = 0.4f), fontSize = 10.5.sp, letterSpacing = 1.4.sp)
        Spacer(Modifier.height(12.dp))
        Text(value, fontFamily = geist, color = Cream, fontSize = 18.sp, fontWeight = FontWeight.Medium, letterSpacing = (-0.1).sp, lineHeight = 22.sp)
        Spacer(Modifier.height(6.dp))
        Text(sub, fontFamily = mono, color = Cream.copy(alpha = 0.55f), fontSize = 13.sp)
    }
}

// ── Footer ────────────────────────────────────────────────────────────────────

@Composable
private fun JeromeFooter(mono: androidx.compose.ui.text.font.FontFamily) {
    Column(modifier = Modifier.fillMaxWidth().background(Ink)) {
        Box(modifier = Modifier.fillMaxWidth().height(0.5.dp).background(Cream.copy(alpha = 0.12f)))
        Row(modifier = Modifier.fillMaxWidth().padding(horizontal = 24.dp, vertical = 24.dp), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
            Text("© 2026 · Jérôme Robbins, dev", fontFamily = mono, color = Cream.copy(alpha = 0.55f), fontSize = 11.5.sp, letterSpacing = 0.4.sp)
            Text("v 3.0 · robbins.ca/jerome", fontFamily = mono, color = Cream.copy(alpha = 0.55f), fontSize = 11.5.sp, letterSpacing = 0.4.sp)
        }
    }
}

@Preview(device = "id:pixel_tablet")
@Composable
fun JeromePreview() {
    Jerome {}
}
